import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Grid,
  Card,
  Icon,
  CircularProgress,
  IconButton,
  Tooltip,
} from "@mui/material";
import toast from "react-hot-toast";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import DataTable from "examples/Tables/DataTable";
import DatePicker from "components/DatePicker/index";
import ServicesApi from "../../Classes/API/Services/ServicesApi";

export default function ViewServiceBundles() {
  const navigate = useNavigate();
  const [bundles, setBundles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const fetchBundles = async () => {
    setLoading(true);
    try {
      const requestBody = {
        pageNumber: pageNumber,
        pageSize: 7,
        patientName: searchTerm,
        fromDate: fromDate || null,
        toDate: toDate || null,
      };
      const res = await ServicesApi.GetAllBundles(requestBody);
      if (res.status === "Success") {
        setBundles(res.data.list || []);
        setTotalPages(res.data.totalPages || 1);
      }
    } catch (error) {
      console.error("Failed to fetch bundles", error);
    }
    setLoading(false);
  };

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchBundles();
    }, 400);
    return () => clearTimeout(delayDebounceFn);
  }, [pageNumber, searchTerm, fromDate, toDate]);

  const handleDeleteBundle = async (id) => {
    const loadId = toast.loading("Deleting bundle...");
    try {
      const res = await ServicesApi.DeleteBundle(id);
      if (res) {
        toast.success("Bundle deleted!", { id: loadId });
        fetchBundles();
      } else {
        toast.error(res.message || "Failed to delete", { id: loadId });
      }
    } catch (err) {
      toast.error("Network error", { id: loadId });
    }
  };

  const columns = [
    {
      Header: "Creation Date",
      accessor: "createdAt",
      width: "15%",
      Cell: ({ value }) => (
        <MDTypography variant="button" fontWeight="bold" color="info">
          {new Date(value).toLocaleDateString("en-GB")}
        </MDTypography>
      ),
    },
    {
      Header: "Patient Name",
      accessor: "patientName",
      width: "30%",
      Cell: ({ value }) => (
        <MDTypography variant="button" fontWeight="medium">
          {value}
        </MDTypography>
      ),
    },
    {
      Header: "Items",
      accessor: "itemsCount",
      width: "15%",
      Cell: ({ value }) => (
        <MDBox display="flex" alignItems="center">
          <Icon
            fontSize="small"
            sx={{ mr: 1, color: value > 0 ? "success" : "error" }}
          >
            {value > 0 ? "list_alt" : "warning"}
          </Icon>
          <MDTypography variant="caption" color="text" fontWeight="medium">
            {value} Services
          </MDTypography>
        </MDBox>
      ),
    },
    {
      Header: "Linked Package",
      accessor: "packageName",
      width: "20%",
      Cell: ({ row }) => {
        const isLinked = !!row.original.packageId;
        return (
          <MDBox display="flex" alignItems="center" gap={0.5}>
            <Icon
              fontSize="small"
              color={isLinked ? "success" : "secondary"}
              sx={{ verticalAlign: "middle" }}
            >
              {isLinked ? "inventory_2" : "link_off"}
            </Icon>
            {isLinked ? (
              <Tooltip title={`Package ID: ${row.original.packageId}`}>
                <MDTypography variant="button" fontWeight="bold" color="success">
                  {row.original.packageName || `Package #${row.original.packageId}`}
                </MDTypography>
              </Tooltip>
            ) : (
              <MDTypography variant="caption" color="text" fontWeight="medium">
                Not Linked
              </MDTypography>
            )}
          </MDBox>
        );
      },
    },
    {
      Header: "Actions",
      accessor: "actions",
      width: "20%",
      Cell: ({ row }) => (
        <MDBox display="flex" gap={1}>
          <MDButton
            variant="gradient"
            color="info"
            size="small"
            onClick={() =>
              navigate(`/ViewServiceBundles/BundleDetails/${row.original.id}`)
            }
          >
            <Icon>visibility</Icon>&nbsp;View
          </MDButton>

          {row.original.itemsCount === 0 ? (
            <IconButton
              color="error"
              size="small"
              onClick={() => handleDeleteBundle(row.original.id)}
            >
              <Icon>delete_outline</Icon>
            </IconButton>
          ) : (
            <span>
              <IconButton color="default" size="small" disabled>
                <Icon>delete_outline</Icon>
              </IconButton>
            </span>
          )}
        </MDBox>
      ),
    },
  ];

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pb={3} pt={3}>
        <Card
          sx={{
            borderRadius: "16px",
            boxShadow: "0 8px 16px rgba(0,0,0,0.05)",
          }}
        >
          <MDBox p={3}>
            <MDBox
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              flexWrap="wrap"
              gap={2}
              mb={3}
            >
              <MDBox>
                <MDTypography variant="h4" fontWeight="bold">
                  Service Bundles
                </MDTypography>
                <MDTypography variant="button" color="text">
                  Manage recurring and daily service plans
                </MDTypography>
              </MDBox>
              <MDButton
                variant="gradient"
                color="dark"
                onClick={() => navigate("/NewServiceBundle")}
              >
                <Icon sx={{ fontWeight: "bold" }}>add</Icon>&nbsp;New Bundle
              </MDButton>
            </MDBox>

            <Grid container spacing={2} component={MDBox} mb={2}>
              <Grid item xs={12} md={4}>
                <MDInput
                  label="Search patient..."
                  placeholder="Patient's name only"
                  fullWidth
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setPageNumber(1);
                  }}
                />
              </Grid>
              <Grid item xs={6} md={3}>
                <DatePicker
                  label="From Date"
                  selected={fromDate}
                  onChange={(date) => {
                    setFromDate(date);
                    setPageNumber(1);
                  }}
                />
              </Grid>
              <Grid item xs={6} md={3}>
                <DatePicker
                  label="To Date"
                  selected={toDate}
                  onChange={(date) => {
                    setToDate(date);
                    setPageNumber(1);
                  }}
                />
              </Grid>
              <Grid item xs={12} md={2} display="flex" alignItems="center">
                <MDButton
                  variant="outlined"
                  color="error"
                  onClick={() => {
                    setFromDate("");
                    setToDate("");
                    setSearchTerm("");
                  }}
                  fullWidth
                >
                  <Icon>filter_alt_off</Icon>&nbsp;Clear
                </MDButton>
              </Grid>
            </Grid>
          </MDBox>

          <MDBox pt={1} position="relative">
            {loading && (
              <MDBox
                position="absolute"
                top="0"
                left="0"
                width="100%"
                height="100%"
                display="flex"
                justifyContent="center"
                alignItems="center"
                zIndex={1}
                sx={{ backgroundColor: "rgba(255,255,255,0.6)" }}
              >
                <CircularProgress color="info" />
              </MDBox>
            )}
            <DataTable
              table={{ columns, rows: bundles }}
              showTotalEntries={false}
              entriesPerPage={false}
              isSorted={false}
              noEndBorder={true}
            />
          </MDBox>

          <MDBox
            display="flex"
            justifyContent="center"
            alignItems="center"
            p={3}
            gap={2}
          >
            <MDButton
              variant="outlined"
              color="info"
              size="small"
              iconOnly
              disabled={pageNumber === 1 || loading}
              onClick={() => setPageNumber((prev) => prev - 1)}
            >
              <Icon>chevron_left</Icon>
            </MDButton>
            <MDTypography variant="button" fontWeight="bold">
              Page {pageNumber} of {totalPages}
            </MDTypography>
            <MDButton
              variant="outlined"
              color="info"
              size="small"
              iconOnly
              disabled={pageNumber === totalPages || loading}
              onClick={() => setPageNumber((prev) => prev + 1)}
            >
              <Icon>chevron_right</Icon>
            </MDButton>
          </MDBox>
        </Card>
      </MDBox>
    </DashboardLayout>
  );
}
